import * as yup from "yup";

import {
  EXPORT_COMPRESSION_LEVEL,
  EXPORT_FILE_FORMAT,
} from "@/components/graphql/enums";
import {
  ExportCompressionLevel,
  ExportFileFormat,
  ExportVideoCodec,
} from "@/components/graphql/schema.graphql.d";
import { createOptionsFromEnumArray } from "@/utils/enums";

export type ExportModality = "image" | "video" | null;

export const PNG_SEQUENCE_FORMAT = "PNG_SEQUENCE" as const;
export type VideoExportFormat = ExportFileFormat | typeof PNG_SEQUENCE_FORMAT;

export const EXPORT_FORM_FIELDS = {
  COMPRESSION: "compression",
  EXPORT_HEIGHT: "exportHeight",
  EXPORT_SIZE: "exportSize",
  EXPORT_WIDTH: "exportWidth",
  FORMAT: "format",
  FPS: "fps",
  FRAME_EXTRACTION_FPS: "frameExtractionFps",
  ORIGINAL_ASPECT_RATIO: "originalAspectRatio",
  ORIGINAL_HEIGHT: "originalHeight",
  ORIGINAL_WIDTH: "originalWidth",
  REMOVE_BACKGROUND: "removeBackground",
  SAVE_SELECTIONS: "saveSelections",
  VIDEO_CODEC: "videoCodec",
} as const;

export interface ExportFormState {
  compression?: ExportCompressionLevel;
  exportHeight?: number;
  exportWidth?: number;
  exportSize: number;
  format: VideoExportFormat;
  fps?: number;
  frameExtractionFps?: number;
  originalAspectRatio?: number;
  originalHeight?: number;
  originalWidth?: number;
  removeBackground: boolean;
  saveSelections: boolean;
  videoCodec?: ExportVideoCodec;
}

export const EXPORT_FORMAT_OPTIONS = createOptionsFromEnumArray(
  EXPORT_FILE_FORMAT,
  true,
).filter((option) => ["JPEG", "PNG", "PSD"].includes(option.value));

export const VIDEO_FORMAT_OPTIONS: {
  value: VideoExportFormat;
  label: string;
}[] = [
  ...createOptionsFromEnumArray(EXPORT_FILE_FORMAT, true).filter((option) =>
    ["MP4", "WEBM", "MOV", "GIF"].includes(option.value),
  ),
  { value: PNG_SEQUENCE_FORMAT, label: "PNG SEQUENCE" },
];

/**
 * Stands in for an unset codec or compression inside a select. It exists only at that
 * boundary: the form field and the mutation both carry `undefined`, which is what lets an
 * MP4 export the source's own bytes instead of transcoding them.
 *
 * The two selects label it differently because it promises them different things. Unset
 * compression is honoured exactly, so it reads "Original". An unset codec is only honoured
 * while nothing else forces a re-encode — ask for High compression and the server picks
 * H.264 — so it reads "Automatic".
 */
export const ENCODING_UNSET = "ORIGINAL" as const;

export const VIDEO_CODEC_OPTIONS: { value: ExportVideoCodec; label: string }[] =
  [
    { value: "H264", label: "H.264" },
    { value: "H265", label: "H.265" },
  ];

export const COMPRESSION_OPTIONS = createOptionsFromEnumArray(
  EXPORT_COMPRESSION_LEVEL,
  true,
);

export const DEFAULT_GIF_FPS = 10;
export const GIF_FPS_OPTIONS = [5, 10, 15, 20, 25, 30, 35, 40, 45, 50];

export const DEFAULT_FRAME_EXTRACTION_FPS = 10;
export const FRAME_EXTRACTION_FPS_OPTIONS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];

export const SIZE_STEP_PERCENT = 25;
export const SIZE_MIN_PERCENT = 1;
export const SIZE_MAX_PERCENT = 800;
export const SIZE_PRESETS = [
  25, 50, 75, 100, 150, 200, 300, 400, 500, 600, 700, 800,
];

const commonSchema = {
  [EXPORT_FORM_FIELDS.SAVE_SELECTIONS]: yup
    .boolean()
    .required("Save selections is required."),
};

const imageValidationSchema = yup.object().shape({
  ...commonSchema,
  [EXPORT_FORM_FIELDS.EXPORT_HEIGHT]: yup
    .number()
    .nullable()
    .min(1, "Height must be at least 1 pixel.")
    .typeError("Height must be a valid number."),
  [EXPORT_FORM_FIELDS.EXPORT_WIDTH]: yup
    .number()
    .nullable()
    .min(1, "Width must be at least 1 pixel.")
    .typeError("Width must be a valid number."),
  [EXPORT_FORM_FIELDS.EXPORT_SIZE]: yup.number().nullable(),
  [EXPORT_FORM_FIELDS.FORMAT]: yup
    .string()
    .oneOf(["JPEG", "PNG", "PSD"] as ExportFileFormat[])
    .required("Format is required."),
  [EXPORT_FORM_FIELDS.ORIGINAL_ASPECT_RATIO]: yup.number().nullable(),
  [EXPORT_FORM_FIELDS.ORIGINAL_HEIGHT]: yup.number().nullable(),
  [EXPORT_FORM_FIELDS.ORIGINAL_WIDTH]: yup.number().nullable(),
  [EXPORT_FORM_FIELDS.REMOVE_BACKGROUND]: yup
    .boolean()
    .required("Remove background is required."),
}) as yup.ObjectSchema<ExportFormState>;

const videoValidationSchema = yup.object().shape({
  ...commonSchema,
  [EXPORT_FORM_FIELDS.FORMAT]: yup
    .string()
    .oneOf([
      "MP4",
      "WEBM",
      "MOV",
      "GIF",
      PNG_SEQUENCE_FORMAT,
    ] as VideoExportFormat[])
    .required("Format is required."),
  [EXPORT_FORM_FIELDS.VIDEO_CODEC]: yup
    .string()
    .oneOf(["H264", "H265"] as ExportVideoCodec[])
    .optional(),
  [EXPORT_FORM_FIELDS.COMPRESSION]: yup
    .string()
    .oneOf(["NORMAL", "HIGH"] as ExportCompressionLevel[])
    .when(EXPORT_FORM_FIELDS.FORMAT, {
      is: (val: string) => val === "GIF" || val === PNG_SEQUENCE_FORMAT,
      then: (schema) => schema.optional(),
      otherwise: (schema) => schema.required("Compression is required."),
    }),
  [EXPORT_FORM_FIELDS.FPS]: yup
    .number()
    .integer("FPS must be an integer.")
    .min(1, "FPS must be at least 1.")
    .max(50, "FPS must be at most 50.")
    .when(EXPORT_FORM_FIELDS.FORMAT, {
      is: "GIF",
      then: (schema) => schema.required("FPS is required."),
      otherwise: (schema) => schema.optional(),
    }),
  [EXPORT_FORM_FIELDS.FRAME_EXTRACTION_FPS]: yup
    .number()
    .integer("FPS must be an integer.")
    .min(1, "FPS must be at least 1.")
    .max(10, "FPS must be at most 10.")
    .when(EXPORT_FORM_FIELDS.FORMAT, {
      is: PNG_SEQUENCE_FORMAT,
      then: (schema) => schema.required("Frame rate is required."),
      otherwise: (schema) => schema.optional(),
    }),
}) as yup.ObjectSchema<ExportFormState>;

export const getValidationSchema = (
  modality: ExportModality,
): yup.ObjectSchema<ExportFormState> => {
  if (modality === "video") return videoValidationSchema;
  return imageValidationSchema;
};

export const EXPORT_DIALOG_FORM_DEFAULT_VALUES = {
  [EXPORT_FORM_FIELDS.EXPORT_SIZE]: 1, // 100%
  [EXPORT_FORM_FIELDS.FORMAT]: "PNG" as ExportFileFormat,
  [EXPORT_FORM_FIELDS.REMOVE_BACKGROUND]: false,
  [EXPORT_FORM_FIELDS.SAVE_SELECTIONS]: false,
};

export const EXPORT_DIALOG_VIDEO_FORM_DEFAULT_VALUES = {
  [EXPORT_FORM_FIELDS.FORMAT]: "MP4" as VideoExportFormat,
  [EXPORT_FORM_FIELDS.FPS]: DEFAULT_GIF_FPS,
  [EXPORT_FORM_FIELDS.FRAME_EXTRACTION_FPS]: DEFAULT_FRAME_EXTRACTION_FPS,
  // Unset: the export keeps the source's encoding unless the user asks for another.
  [EXPORT_FORM_FIELDS.VIDEO_CODEC]: undefined,
  [EXPORT_FORM_FIELDS.COMPRESSION]: undefined,
  [EXPORT_FORM_FIELDS.SAVE_SELECTIONS]: false,
};
